import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useUser, useAuth as useClerkAuth } from '@clerk/clerk-react';
import api from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const { isSignedIn, isLoaded: clerkLoaded } = useClerkAuth();
  const { user: clerkUser } = useUser();
  const { getToken, signOut } = useClerkAuth();

  const [user, setUser] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [needsPrivacyNotice, setNeedsPrivacyNotice] = useState(false);
  const [requiredPrivacyVersion, setRequiredPrivacyVersion] = useState(null);

  // Store getToken globally so api.js can attach Bearer tokens
  useEffect(() => {
    if (getToken) {
      window.__clerkGetToken = getToken;
    }
  }, [getToken]);

  // When Clerk session becomes active, fetch the VCIN user profile
  useEffect(() => {
    if (!clerkLoaded) return;
    if (!isSignedIn) {
      setUser(null);
      setNeedsPrivacyNotice(false);
      setRequiredPrivacyVersion(null);
      setIsInitializing(false);
      return;
    }

    const fetchProfile = async () => {
      try {
        const response = await api.get('/auth/me');
        setUser(response.data);
        setNeedsPrivacyNotice(false);
      } catch (err) {
        if (err.statusCode === 409 || err.code === 'PRIVACY_NOTICE_UPDATE_REQUIRED') {
          // Privacy notice acceptance required — show notice screen
          setNeedsPrivacyNotice(true);
          setRequiredPrivacyVersion(err.required_version || null);
          setUser(null);
        } else if (err.statusCode === 401 || err.statusCode === 403 || !err.statusCode) {
          // 401, 403 (suspended/disabled), or network/CORS error — Clerk session
          // exists but the backend rejects it. Sign out completely so the user
          // lands on a clean login screen rather than looping forever.
          setUser(null);
          setNeedsPrivacyNotice(false);
          await signOut();
        } else {
          // Other error (5xx, etc.) — don't sign out, may be transient
          setUser(null);
        }
      } finally {
        setIsInitializing(false);
      }
    };

    fetchProfile();
  }, [clerkLoaded, isSignedIn, clerkUser?.id]);

  // PF-006 / Stage 3 Step 5: Detect first PWA standalone open and notify backend.
  // Once per session: if the user is an OFFICER and the app is running in
  // standalone mode (installed PWA), POST to /me/installed which
  // records officers.first_pwa_open_at on first call (idempotent thereafter).
  useEffect(() => {
    if (!user) return;
    if (user.role !== 'OFFICER' && user.role !== 'INDEPENDENT_OFFICER') return;

    const SESSION_KEY = 'vcin_pwa_install_pinged';
    if (sessionStorage.getItem(SESSION_KEY) === '1') return;

    // Detect standalone mode. Two checks because iOS Safari uses a non-standard
    // window.navigator.standalone property instead of the media query.
    const standaloneViaMatchMedia = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(display-mode: standalone)').matches;
    const standaloneViaIosSafari = typeof window !== 'undefined'
      && window.navigator
      && window.navigator.standalone === true;

    if (!standaloneViaMatchMedia && !standaloneViaIosSafari) return;

    (async () => {
      try {
        await api.post('/me/installed');
        sessionStorage.setItem(SESSION_KEY, '1');
      } catch (err) {
        // Non-fatal — backend is idempotent and the officer can still use the app.
        // Don't set the session flag so we retry on next session.
        console.warn('[PWA] install detection ping failed:', err.message);
      }
    })();
  }, [user]);

  const acceptPrivacyNotice = useCallback(async (version) => {
    await api.post('/auth/accept-privacy', { version });
    // Re-fetch profile — should now pass the 409 check
    const response = await api.get('/auth/me');
    setUser(response.data);
    setNeedsPrivacyNotice(false);
    setRequiredPrivacyVersion(null);
  }, []);

  const fetchUser = useCallback(async () => {
    if (!isSignedIn) return null;
    const response = await api.get('/auth/me');
    setUser(response.data);
    return response.data;
  }, [isSignedIn]);

  const logout = useCallback(async () => {
    setUser(null);
    setNeedsPrivacyNotice(false);
    setRequiredPrivacyVersion(null);
    await signOut();
  }, [signOut]);

  const value = {
    user,
    isAuthenticated: !!isSignedIn && !!user,
    isInitializing: !clerkLoaded || isInitializing,
    needsPrivacyNotice,
    requiredPrivacyVersion,
    acceptPrivacyNotice,
    fetchUser,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
